import { jsxs, jsx } from "react/jsx-runtime";
import { Box, Paragraph, FormHelp, TextButton } from "@zillow/constellation-10";
import { yieldCallback } from "@zillow/yield-callback";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { commaSeparateNumber } from "../../utils/zhl-calculator.esm.js";
import TextField from "../Fields/TextField.esm.js";
import { handleFieldChangeEvent } from "./DTICalculator.clickstream.esm.js";
import { FieldConfig } from "./DTICalculator.utils.esm.js";
import { DTI_CALC, MODAL_DIALOG, TOOLTIP } from "./constant.esm.js";
import { trackLabel } from "./trackLabel.esm.js";
function DTIFields({
  className
}) {
  const [advanced, setAdvanced] = useState(false);
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsxs(Box, { css: {
      marginBottom: "default"
    }, children: [
      /* @__PURE__ */ jsx(TextField, { label: "Annual income", name: FieldConfig.annualIncome.name, leftAdornment: "$", config: FieldConfig.annualIncome.config, autoComplete: "off", format: commaSeparateNumber, required: true, tooltip: {
        title: "Annual income",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "Your total annual earnings before taxes, for you and your co-borrower. Annual income includes your base salary and any additional commissions, bonuses, tips, and investment income." }),
        interactionEventCmsInfo: {
          block_type_cd: MODAL_DIALOG,
          block_id: DTI_CALC,
          block_displayed_txt: "Annual income"
        }
      }, onBlur: () => {
        trackLabel("Annual Income Edit");
      }, onChange: () => {
        handleFieldChangeEvent("Annual income", "form_field");
      } }),
      /* @__PURE__ */ jsx(FormHelp, { css: {
        marginTop: "tighter",
        color: "text.subtle"
      }, children: "Include co-borrower's salary" })
    ] }),
    /* @__PURE__ */ jsx(TextField, { label: "Min. credit card payments", name: FieldConfig.minCreditCardPayments.name, leftAdornment: "$", rightAdornment: "/month", config: FieldConfig.minCreditCardPayments.config, autoComplete: "off", format: commaSeparateNumber, required: true, tooltip: {
      title: "Minimum credit card payments",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "The lowest amount of money that you are required to pay on your credit card each month, as appears on your monthly statement. Do not include credit card balances that you pay off in full each month." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: DTI_CALC,
        block_displayed_txt: "Min. credit card payments"
      }
    }, onBlur: () => {
      trackLabel("Min Credit Card Payment Edit");
    }, onChange: () => {
      handleFieldChangeEvent("Min. credit card payments", "form_field");
    } }),
    /* @__PURE__ */ jsx(TextField, { label: "Car loan payments", name: FieldConfig.carLoanPayments.name, leftAdornment: "$", rightAdornment: "/month", config: FieldConfig.carLoanPayments.config, autoComplete: "off", format: commaSeparateNumber, required: true, tooltip: {
      title: "Car loan payments",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "The total amount you pay each month on any car loans. Enter $0 if you do not have a car loan or loan or have paid or plan to pay the loan balance off in full before applying for a mortgage." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: DTI_CALC,
        block_displayed_txt: "Car loan payments"
      }
    }, onBlur: () => {
      trackLabel("Car Loan Payment Edit");
    }, onChange: () => {
      handleFieldChangeEvent("Car loan payments", "form_field");
    } }),
    /* @__PURE__ */ jsx(TextField, { label: "Student loan payments", name: FieldConfig.studentLoanPayments.name, leftAdornment: "$", rightAdornment: "/month", config: FieldConfig.studentLoanPayments.config, autoComplete: "off", format: commaSeparateNumber, required: true, tooltip: {
      title: "Student loan payments",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "The total amount you pay each month on student loans. Enter $0 if you do not have a student loan or have paid or plan to pay the loan balance off in full before applying for a mortgage." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: DTI_CALC,
        block_displayed_txt: "Student loan payments"
      }
    }, onBlur: () => {
      trackLabel("Student Loan Payment Edit");
    }, onChange: () => {
      handleFieldChangeEvent("Student loan payments", "form_field");
    } }),
    /* @__PURE__ */ jsx(Box, { css: {
      marginBottom: "tighter"
    }, children: /* @__PURE__ */ jsx(TextButton, { type: "button", onClick: yieldCallback(() => {
      setAdvanced(!advanced);
      if (advanced) {
        trackLabel("Show Simple Calc");
        handleFieldChangeEvent("See less", "toggle");
      } else {
        trackLabel("Show Advanced Calc");
        handleFieldChangeEvent("See more", "toggle");
      }
    }), children: advanced ? "See less" : "See more" }) }),
    advanced && /* @__PURE__ */ jsxs(React.Fragment, { children: [
      /* @__PURE__ */ jsx(TextField, { label: "Alimony/child support payments", name: FieldConfig.alimonySupportPayments.name, leftAdornment: "$", rightAdornment: "/month", config: FieldConfig.alimonySupportPayments.config, autoComplete: "off", format: commaSeparateNumber, required: true, tooltip: {
        title: "Alimony/child support payments",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "If you make payments every month for alimony or child support, enter the total amount amount paid." }),
        interactionEventCmsInfo: {
          block_type_cd: TOOLTIP,
          block_id: DTI_CALC,
          block_displayed_txt: "Alimony/child support payments"
        }
      }, onBlur: () => {
        trackLabel("Alimony or Child Supports Payment Edit");
      }, onChange: () => {
        handleFieldChangeEvent("Alimony/child support payments", "form_field");
      } }),
      /* @__PURE__ */ jsx(TextField, { label: "Secondary home expenses", name: FieldConfig.secondaryHomeExpenses.name, leftAdornment: "$", rightAdornment: "/month", config: FieldConfig.secondaryHomeExpenses.config, autoComplete: "off", format: commaSeparateNumber, required: true, tooltip: {
        title: "Secondary home expenses",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "The total amount of mortgage payments or maintenance costs paid on additional vacation or rental property that you may own. Do not include your new mortgage payments, current mortgage or rent that you'll no longer pay after buying a new home." }),
        interactionEventCmsInfo: {
          block_type_cd: TOOLTIP,
          block_id: DTI_CALC,
          block_displayed_txt: "Secondary home expenses"
        }
      }, onBlur: () => {
        trackLabel("Secondary Home Expense Edit");
      }, onChange: () => {
        handleFieldChangeEvent("Secondary home expenses", "form_field");
      } }),
      /* @__PURE__ */ jsx(TextField, { label: "Other loan or debt payments", name: FieldConfig.otherDebtPayments.name, leftAdornment: "$", rightAdornment: "/month", config: FieldConfig.otherDebtPayments.config, autoComplete: "off", format: commaSeparateNumber, required: true, tooltip: {
        title: "Other loan or debt payments",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "The total amount of additional debt obligations, such as personal loans. Do not include your new mortgage payments, current mortgage or rent that you'll no longer pay after buying a new home." }),
        interactionEventCmsInfo: {
          block_type_cd: TOOLTIP,
          block_id: DTI_CALC,
          block_displayed_txt: "Other loan or debt payments"
        }
      }, onBlur: () => {
        trackLabel("Other Loan or Debt Payment Edit");
      }, onChange: () => {
        handleFieldChangeEvent("Other loan or debt payments", "form_field");
      } })
    ] })
  ] });
}
DTIFields.defaultProps = {
  className: void 0
};
DTIFields.propTypes = {
  className: PropTypes.string
};
export {
  DTIFields as default
};
